import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { HomeButton } from '../components/NavButtons';
import { getCurrencySymbol, getSymbol } from '../utils/currency';
import { usePlatformLogo, usePlatformSettings } from '../hooks/usePlatformSettings';
import QrScannerComponent from '../components/QrScannerComponent';
import SellerSubscriptionCard from '../components/SellerSubscriptionCard';
import PayoutMethodsCard from '../components/PayoutMethodsCard';
import SellerPaymentMethodsCard from '../components/SellerPaymentMethodsCard';
import StudioSettingsCard from '../components/StudioSettingsCard';
import { BookingRow } from '../components/seller/BookingRow';
import { LiveSessionsBookings } from '../components/seller/LiveSessionsBookings';
import { ListingRow } from '../components/seller/ListingRow';
import { StatCard } from '../components/seller/StatCard';
import {
  House, List, CalendarBlank, Wallet, User, Plus, ChartLineUp, Star,
  Clock, Check, X, CaretRight, Eye, Pencil, GridFour, CalendarDots,
  MagnifyingGlass, Bell, FunnelSimple, ArrowsClockwise, CheckCircle,
  XCircle, Hourglass, MapPin, Users, Timer, ListBullets, QrCode, Camera,
  Megaphone, VideoCamera
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SellerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isSeller, loading: authLoading } = useAuth();
  const LOGO_URL = usePlatformLogo();
  const platformSettings = usePlatformSettings();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [qrScanResult, setQrScanResult] = useState(null);
  const [manualCode, setManualCode] = useState({ booking_id: '', code: '' });

  // Live Sessions (under Bookings)
  const [bookingView, setBookingView] = useState('space'); // 'space' | 'live'
  const [sellerClasses, setSellerClasses] = useState([]);
  const [expandedClassId, setExpandedClassId] = useState(null);
  const [participantsByClass, setParticipantsByClass] = useState({});
  const [participantLoading, setParticipantLoading] = useState(null);

  // Determine active tab from URL
  const activeTab = useMemo(() => {
    if (location.pathname.includes('/seller/bookings')) return 'bookings';
    if (location.pathname.includes('/seller/listings')) return 'listings';
    return 'dashboard';
  }, [location.pathname]);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, bookingsRes, listingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/seller/stats`),
        axios.get(`${API_URL}/api/bookings?as_seller=true`),
        axios.get(`${API_URL}/api/listings/my-listings`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data.bookings || []);
      setListings(listingsRes.data.listings || []);
    } catch (error) {
      console.error('Error fetching seller data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && isSeller) fetchData();
  }, [user, isSeller, fetchData]);

  const handleBookingAction = async (bookingId, action) => {
    setActionLoading(bookingId);
    try {
      await axios.post(`${API_URL}/api/bookings/${bookingId}/${action}`, {});
      await fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCheckIn = async (bookingId, code) => {
    try {
      const res = await axios.post(`${API_URL}/api/bookings/check-in`, { booking_id: bookingId, code });
      setQrScanResult({ success: true, message: res.data.message });
      await fetchData();
    } catch (e) {
      setQrScanResult({ success: false, message: e.response?.data?.detail || 'Check-in failed' });
    }
  };

  const handleQrScan = (decodedText) => {
    try {
      const data = JSON.parse(decodedText);
      if (data.booking_id && data.code) {
        handleCheckIn(data.booking_id, data.code);
      } else {
        setQrScanResult({ success: false, message: 'Invalid QR code format' });
      }
    } catch {
      setQrScanResult({ success: false, message: 'Could not read QR code' });
    }
  };

  const fetchSellerClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/live-classes/my`);
      setSellerClasses(res.data.seller_classes || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchParticipants = async (classId) => {
    setParticipantLoading(classId);
    try {
      const res = await axios.get(`${API_URL}/api/live-classes/${classId}/participants`);
      setParticipantsByClass(prev => ({ ...prev, [classId]: res.data.participants || [] }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load participants');
    } finally {
      setParticipantLoading(null);
    }
  };

  const toggleClassExpand = (classId) => {
    if (expandedClassId === classId) {
      setExpandedClassId(null);
    } else {
      setExpandedClassId(classId);
      if (!participantsByClass[classId]) fetchParticipants(classId);
    }
  };

  const handleToggleParticipantBlock = async (classId, userId, currentlyBlocked) => {
    const action = currentlyBlocked ? 'unblock' : 'block';
    try {
      await axios.post(`${API_URL}/api/live-classes/${classId}/participants/${userId}/${action}`);
      setParticipantsByClass(prev => ({
        ...prev,
        [classId]: (prev[classId] || []).map(p =>
          p.user_id === userId ? { ...p, blocked: !currentlyBlocked } : p
        ),
      }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    }
  };

  useEffect(() => {
    if (activeTab === 'bookings' && bookingView === 'live' && user && isSeller) {
      fetchSellerClasses();
    }
  }, [activeTab, bookingView, user, isSeller, fetchSellerClasses]);

  const filteredBookings = useMemo(() => {
    if (bookingFilter === 'all') return bookings;
    return bookings.filter(b => b.status === bookingFilter);
  }, [bookings, bookingFilter]);

  const pendingCount = bookings.filter(b => b.status === 'pending_approval' || b.status === 'pending_payment').length;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user || !isSeller) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700 mb-4">
            {!user ? 'Please sign in to access seller dashboard' : 'Please upgrade to seller account'}
          </h2>
          <Button onClick={() => navigate('/seller/login')} className="bg-emerald-500 hover:bg-emerald-600 rounded-full">
            {user ? 'Become a Seller' : 'Sign In as Host'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="seller-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-1">
              <HomeButton />
              <Link to="/seller" className="flex items-center gap-2" data-testid="seller-logo">
                <img src={LOGO_URL} alt="Look4Space" className="h-10 w-auto object-contain" />
                <span className="text-lg font-semibold text-emerald-700 hidden sm:block">Host Portal</span>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/buyer')} className="text-slate-600 hover:text-blue-600" data-testid="switch-to-buyer-btn">
                <MagnifyingGlass size={20} className="mr-2" />
                <span className="hidden sm:inline">Buyer Mode</span>
              </Button>
              <button className="p-2 hover:bg-slate-100 rounded-full relative" data-testid="notifications-btn">
                <Bell size={22} className="text-slate-600" />
                {pendingCount > 0 && (
                  <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">{pendingCount}</span>
                )}
              </button>
              <button onClick={() => navigate('/profile')} className="w-10 h-10 rounded-full overflow-hidden" data-testid="profile-btn">
                {user.profile_photo ? (
                  <img src={user.profile_photo?.startsWith('http') ? user.profile_photo : `${API_URL}${user.profile_photo}`} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-emerald-100 flex items-center justify-center">
                    <User size={20} className="text-emerald-600" />
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 -mb-px">
            {[
              { key: 'dashboard', label: 'Overview', path: '/seller', icon: ChartLineUp },
              { key: 'slots', label: 'Slot Manager', path: '/seller/slots', icon: GridFour },
              { key: 'bookings', label: 'Bookings', path: '/seller/bookings', icon: CalendarBlank, badge: pendingCount },
              { key: 'listings', label: 'My Listings', path: '/seller/listings', icon: House },
              { key: 'live-classes', label: 'Live Sessions', path: '/live-classes', icon: VideoCamera },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => navigate(tab.path)}
                data-testid={`tab-${tab.key}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-emerald-500 text-emerald-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <tab.icon size={18} weight={activeTab === tab.key ? 'fill' : 'regular'} />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{tab.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* ==================== DASHBOARD TAB ==================== */}
        {activeTab === 'dashboard' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']">
                  Welcome back, {user.name?.split(' ')[0]}!
                </h1>
                <p className="text-slate-500 mt-1">Here's what's happening with your spaces</p>
              </div>
              <div className="flex items-center gap-2">
              <Button onClick={() => navigate('/seller/add-listing')}
                className="bg-emerald-500 hover:bg-emerald-600 rounded-xl h-11 px-5 font-semibold shadow-lg shadow-emerald-500/20"
                data-testid="add-listing-btn">
                <Plus size={18} className="mr-2" weight="bold" /> Add Listing
              </Button>
              {platformSettings.seller_paid_ads !== false && (
              <Button onClick={() => navigate('/seller/ads')} variant="outline"
                className="rounded-xl h-11 px-5 font-semibold border-orange-300 text-orange-600 hover:bg-orange-50"
                data-testid="promote-btn">
                <Megaphone size={18} className="mr-2" weight="bold" /> Promote
              </Button>
              )}
              <Button onClick={() => navigate('/live-classes')} variant="outline"
                className="rounded-xl h-11 px-5 font-semibold border-pink-300 text-pink-600 hover:bg-pink-50"
                data-testid="live-classes-btn">
                <VideoCamera size={18} className="mr-2" weight="bold" /> Live Sessions
              </Button>
              </div>
            </div>

            {/* Subscription status card */}
            <div className="mb-6">
              <SellerSubscriptionCard />
            </div>

            {/* Payout methods (informational) */}
            <div className="mb-6">
              <PayoutMethodsCard />
            </div>

            {/* Seller's own direct payment methods */}
            <div className="mb-6">
              <SellerPaymentMethodsCard />
            </div>

            {/* Stats Grid */}
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[...Array(4)].map((_, i) => (
                  <div key={`seller-stats-skeleton-${i}`} className="bg-white rounded-2xl p-5 animate-pulse"><div className="h-4 bg-slate-200 rounded w-1/2 mb-3"></div><div className="h-8 bg-slate-200 rounded w-3/4"></div></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard title="Revenue" value={`${getCurrencySymbol(user?.country)}${stats?.total_revenue?.toLocaleString() || 0}`} icon={Wallet} color="bg-emerald-500" testId="stat-revenue" />
                <StatCard title="Active Listings" value={stats?.live_listings || 0} subtext={`of ${stats?.total_listings || 0} total`} icon={House} color="bg-blue-500" testId="stat-listings" />
                <StatCard title="Bookings" value={stats?.total_bookings || 0} subtext={pendingCount > 0 ? `${pendingCount} need action` : 'all handled'} icon={CalendarBlank} color="bg-purple-500" testId="stat-bookings" />
                <StatCard title="Rating" value={stats?.average_rating?.toFixed(1) || '0.0'} icon={Star} color="bg-amber-500" testId="stat-rating" />
              </div>
            )}

            {/* Pending Actions - shown only if there are pending bookings */}
            {pendingCount > 0 && (
              <Card className="border-amber-200 bg-amber-50/50 mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-amber-800 flex items-center gap-2">
                    <Hourglass size={18} weight="fill" /> {pendingCount} Booking(s) Need Your Action
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {bookings.filter(b => b.status === 'pending_approval' || b.status === 'pending_payment').slice(0, 3).map(booking => (
                      <BookingRow key={booking.booking_id} booking={booking} onAction={handleBookingAction} actionLoading={actionLoading} compact />
                    ))}
                  </div>
                  {pendingCount > 3 && (
                    <Button variant="ghost" onClick={() => navigate('/seller/bookings')} className="text-amber-700 mt-2 w-full" data-testid="view-all-pending-btn">
                      View all {pendingCount} pending <CaretRight size={14} />
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quick summary cards */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Recent Bookings Preview */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-semibold">Recent Bookings</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/seller/bookings')} className="text-blue-600 text-sm" data-testid="view-all-bookings-btn">
                    View All <CaretRight size={14} />
                  </Button>
                </CardHeader>
                <CardContent>
                  {bookings.length > 0 ? (
                    <div className="space-y-2">
                      {bookings.slice(0, 4).map(booking => (
                        <BookingRow key={booking.booking_id} booking={booking} onAction={handleBookingAction} actionLoading={actionLoading} compact />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CalendarBlank size={36} className="text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">No bookings yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Listings Preview */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-base font-semibold">My Listings</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/seller/listings')} className="text-blue-600 text-sm" data-testid="view-all-listings-btn">
                    View All <CaretRight size={14} />
                  </Button>
                </CardHeader>
                <CardContent>
                  {listings.length > 0 ? (
                    <div className="space-y-2">
                      {listings.filter(l => l.status === 'live').slice(0, 4).map(listing => (
                        <ListingRow key={listing.listing_id} listing={listing} navigate={navigate} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <House size={36} className="text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm mb-3">No listings yet</p>
                      <Button onClick={() => navigate('/seller/add-listing')} className="bg-emerald-500 hover:bg-emerald-600 rounded-full text-sm">Create First Listing</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* ==================== BOOKINGS TAB ==================== */}
        {activeTab === 'bookings' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-slate-900">{bookingView === 'live' ? 'Live Sessions' : 'All Bookings'}</h1>
              <div className="flex gap-2">
                {bookingView === 'space' && (
                  <Button onClick={() => setShowQrScanner(true)} className="bg-indigo-500 hover:bg-indigo-600 rounded-xl" data-testid="scan-qr-btn">
                    <QrCode size={16} className="mr-2" weight="bold" /> Scan QR
                  </Button>
                )}
                <Button variant="outline" onClick={() => { fetchData(); if (bookingView === 'live') fetchSellerClasses(); }} className="rounded-xl" data-testid="refresh-bookings-btn">
                  <ArrowsClockwise size={16} className="mr-2" /> Refresh
                </Button>
              </div>
            </div>

            {/* Sub-tabs: Space Bookings | Live Sessions */}
            <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-200 mb-5 max-w-md" data-testid="booking-subtabs">
              <button onClick={() => setBookingView('space')}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${bookingView === 'space' ? 'bg-slate-800 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                data-testid="subtab-space">
                <CalendarBlank size={14} /> Space Bookings
              </button>
              <button onClick={() => setBookingView('live')}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${bookingView === 'live' ? 'bg-pink-500 text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                data-testid="subtab-live">
                <VideoCamera size={14} weight={bookingView === 'live' ? 'fill' : 'regular'} /> Live Sessions
              </button>
            </div>

            {bookingView === 'space' && (
              <>
                {/* Status Filters */}
                <div className="flex flex-wrap gap-2 mb-6" data-testid="booking-filters">
                  {[
                    { key: 'all', label: 'All', count: bookings.length },
                    { key: 'pending_approval', label: 'Awaiting Approval', count: bookings.filter(b => b.status === 'pending_approval').length },
                    { key: 'pending_payment', label: 'Pending Payment', count: bookings.filter(b => b.status === 'pending_payment').length },
                    { key: 'confirmed', label: 'Confirmed', count: bookings.filter(b => b.status === 'confirmed').length },
                    { key: 'completed', label: 'Completed', count: bookings.filter(b => b.status === 'completed').length },
                    { key: 'cancelled', label: 'Cancelled', count: bookings.filter(b => b.status === 'cancelled').length },
                  ].filter(f => f.key === 'all' || f.count > 0).map(filter => (
                    <button
                      key={filter.key}
                      onClick={() => setBookingFilter(filter.key)}
                      data-testid={`filter-${filter.key}`}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        bookingFilter === filter.key
                          ? 'bg-slate-800 text-white'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {filter.label} ({filter.count})
                    </button>
                  ))}
                </div>

                {/* Bookings List */}
                {filteredBookings.length > 0 ? (
                  <div className="space-y-3">
                    {filteredBookings.map(booking => (
                      <BookingRow key={booking.booking_id} booking={booking} onAction={handleBookingAction} actionLoading={actionLoading} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <CalendarBlank size={48} className="text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-400">No bookings {bookingFilter !== 'all' ? `with status "${bookingFilter.replace('_', ' ')}"` : 'yet'}</p>
                  </div>
                )}
              </>
            )}

            {bookingView === 'live' && (
              <LiveSessionsBookings
                classes={sellerClasses}
                expandedClassId={expandedClassId}
                participantsByClass={participantsByClass}
                participantLoading={participantLoading}
                onToggleExpand={toggleClassExpand}
                onToggleBlock={handleToggleParticipantBlock}
                navigate={navigate}
              />
            )}
          </>
        )}

        {/* ==================== LISTINGS TAB ==================== */}
        {activeTab === 'listings' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-slate-900">My Listings ({listings.length})</h1>
              <Button onClick={() => navigate('/seller/add-listing')}
                className="bg-emerald-500 hover:bg-emerald-600 rounded-xl h-10 px-5 font-semibold"
                data-testid="add-listing-btn-tab">
                <Plus size={16} className="mr-2" weight="bold" /> Add Listing
              </Button>
            </div>

            {listings.length > 0 ? (
              <div className="space-y-3">
                {listings.map(listing => (
                  <ListingRow key={listing.listing_id} listing={listing} navigate={navigate} expanded />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <House size={48} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 mb-4">You haven't created any listings yet</p>
                <Button onClick={() => navigate('/seller/add-listing')} className="bg-emerald-500 hover:bg-emerald-600 rounded-full">
                  Create Your First Listing
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* QR Scanner Modal */}
      {showQrScanner && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="qr-scanner-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><QrCode size={20} /> QR Check-in</h3>
              <button onClick={() => { setShowQrScanner(false); setQrScanResult(null); setManualCode({ booking_id: '', code: '' }); }} className="p-1 hover:bg-slate-100 rounded-lg" data-testid="close-qr-scanner">
                <X size={20} className="text-slate-500" />
              </button>
            </div>

            {qrScanResult ? (
              <div className={`p-4 rounded-xl text-center mb-4 ${qrScanResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`} data-testid="qr-scan-result">
                {qrScanResult.success ? (
                  <CheckCircle size={40} className="text-emerald-500 mx-auto mb-2" />
                ) : (
                  <XCircle size={40} className="text-red-500 mx-auto mb-2" />
                )}
                <p className={`font-semibold ${qrScanResult.success ? 'text-emerald-700' : 'text-red-700'}`}>{qrScanResult.message}</p>
                <button onClick={() => setQrScanResult(null)} className="mt-3 px-4 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700">
                  Scan Another
                </button>
              </div>
            ) : (
              <>
                <QrScannerComponent onScan={handleQrScan} />
                <div className="my-4 flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400">or enter manually</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <div className="space-y-2">
                  <input type="text" value={manualCode.booking_id} onChange={e => setManualCode(p => ({ ...p, booking_id: e.target.value }))}
                    placeholder="Booking ID" className="w-full h-9 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
                    data-testid="manual-booking-id" />
                  <input type="text" value={manualCode.code} onChange={e => setManualCode(p => ({ ...p, code: e.target.value }))}
                    placeholder="Check-in Code" className="w-full h-9 px-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-blue-500 outline-none"
                    data-testid="manual-checkin-code" />
                  <Button onClick={() => { if (manualCode.booking_id && manualCode.code) handleCheckIn(manualCode.booking_id, manualCode.code); }}
                    disabled={!manualCode.booking_id || !manualCode.code}
                    className="w-full bg-indigo-500 hover:bg-indigo-600 rounded-lg" data-testid="manual-checkin-btn">
                    <Check size={14} className="mr-2" /> Verify & Check In
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bottom Navigation - Mobile */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 md:hidden z-40">
        <div className="flex items-center justify-around h-16">
          {[
            { path: '/seller', icon: ChartLineUp, label: 'Dashboard', key: 'dashboard' },
            { path: '/seller/bookings', icon: CalendarBlank, label: 'Bookings', key: 'bookings', badge: pendingCount },
            { path: '/seller/listings', icon: List, label: 'Listings', key: 'listings' },
            { path: '/profile', icon: User, label: 'Profile', key: 'profile' },
          ].map(nav => (
            <Link key={nav.key} to={nav.path}
              className={`flex flex-col items-center gap-1 relative ${activeTab === nav.key ? 'text-emerald-600' : 'text-slate-500'}`}
              data-testid={`nav-${nav.key}`}>
              <nav.icon size={22} weight={activeTab === nav.key ? 'fill' : 'regular'} />
              <span className="text-[10px] font-medium">{nav.label}</span>
              {nav.badge > 0 && (
                <span className="absolute -top-1 right-0 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">{nav.badge}</span>
              )}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};


export default SellerDashboard;
